import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, LockClosedIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import bluelogo from '../assets/bluelogo.png';
import Loading from '../components/ui/Loading';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loading, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsTransitioning(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Eğer kullanıcı zaten giriş yapmışsa proje seçim sayfasına yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/module-selection', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Form validation
    if (!email || !password) {
      setError('Email ve şifre zorunludur.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await login(email, password);

      if (result.success) {
        setIsTransitioning(true);
        setTimeout(() => {
          // Eğer kullanıcı şifre değiştirmek zorundaysa, şifre değiştirme sayfasına yönlendir
          if (result.mustChangePassword) {
            navigate('/change-password', { replace: true });
          } else {
            // Başarılı giriş - proje seçim sayfasına yönlendir
            navigate('/module-selection', { replace: true });
          }
        }, 500);
      } else {
        setError(result.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (error) {
      setError('Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {isTransitioning && (
        <div className="fixed inset-0 z-[100] bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
          <Loading fullScreen={false} message="Sistem Hazırlanıyor..." />
        </div>
      )}

      <div className={`w-full max-w-md relative z-10 transition-all duration-500 ${isTransitioning ? 'opacity-0 invisible scale-95' : 'opacity-100 visible scale-100'}`}>
        {/* Very subtle pattern - barely visible */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(44,62,80,0.15)_1px,_transparent_0)] bg-[length:32px_32px]"></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-xl transition-all duration-300">
            <CardHeader className="space-y-1 text-center pb-8">
              {/* Logo */}
              <div className="mx-auto w-28 h-28 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center mb-6 shadow-md hover:shadow-lg transition-all duration-300 group border border-gray-200 dark:border-gray-600 p-3">
                <img
                  src={bluelogo}
                  alt="HelpDesk Logo"
                  className="w-full h-full object-contain"
                />
              </div>

              <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
                Welcome back
              </CardTitle>
              <CardDescription className="text-gray-600 dark:text-gray-400 text-base">
                Sign in to your HelpDesk workspace
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleLogin}>
              <CardContent className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="flex items-center p-4 text-sm text-error bg-error/10 border border-error/20 rounded-md">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Email Field */}
                <div className="space-y-3">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-900 dark:text-white">
                    Email
                  </Label>
                  <div className="relative group">
                    <EnvelopeIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400 group-focus-within:text-blue-600 transition-colors duration-200" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500/10 transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-3">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-900 dark:text-white">
                    Password
                  </Label>
                  <div className="relative group">
                    <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400 group-focus-within:text-blue-600 transition-colors duration-200" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-11 pr-11 h-12 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500/10 transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors duration-200 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center space-x-3">
                    <input
                      id="remember"
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500/20 focus:ring-2"
                      disabled={isLoading}
                    />
                    <Label htmlFor="remember" className="text-sm text-gray-600 dark:text-gray-400">
                      Remember me
                    </Label>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-400 transition-colors duration-200 hover:underline font-medium disabled:opacity-50"
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-6 pt-6">
                {/* Sign In Button */}
                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Giriş yapılıyor...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                {/* Sign Up Link */}
                <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-800 dark:hover:text-blue-400 transition-colors duration-200 font-medium hover:underline disabled:opacity-50"
                    disabled={isLoading}
                  >
                    Sign up
                  </button>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
